import React, { useState, useEffect } from 'react';
import { Save, Edit3, X } from 'lucide-react';
import { getAboutMe, saveAboutMe } from '../../services/homepageContentService';

const renderInline = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, index) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
      <strong key={index} className="font-semibold text-stone-900">{part.slice(2, -2)}</strong>
    ) : (
      <span key={index}>{part}</span>
    )
  );

const AboutMeEditor = ({ isAdmin, setMessage }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({
    title: "About Me",
    bio: `Hello! I'm a full-stack developer passionate about building simple, elegant solutions to complex problems. My journey into coding started with a curiosity for how the web works, which quickly evolved into a dedicated effort to master modern frameworks like React and Angular.

This blog serves as my digital sandbox where I document my technical explorations, share insights from my coding projects (many of which you can find on the **Web Apps** page!), and discuss the intersection of technology and minimalist design.

Currently, I'm focusing on generative AI applications and pushing the boundaries of single-file web deployment. When I'm not coding, you can find me hiking or learning new languages. Feel free to connect!`,
    skills: [
      "Frontend: React, TypeScript, Tailwind CSS",
      "Backend: Supabase (PostgreSQL), Node.js, Serverless Architectures"
    ]
  });

  useEffect(() => {
    const loadAboutMe = async () => {
      try {
        const data = await getAboutMe();
        if (data) {
          setContent(data);
        }
      } catch (error) {
        console.error('Error loading about me:', error);
      } finally {
        setLoading(false);
      }
    };
    loadAboutMe();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      await saveAboutMe(content);
      setMessage("About Me page updated successfully!");
      setIsEditing(false);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving about me:', error);
      setMessage("Failed to save About Me content. Please try again.");
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (loading && !isEditing) {
    return (
      <div className="p-8 panel">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to check if skills exist and have content
  const hasSkills = content.skills && Array.isArray(content.skills) && content.skills.length > 0 && content.skills.some(skill => skill && skill.trim() !== '');

  if (!isAdmin) {
    // Show read-only version for non-admin users
    return (
      <div className="p-8 panel">
        <h2 className="font-serif text-4xl font-semibold tracking-tight text-stone-900 mb-6 border-b border-stone-200 pb-3">{content.title}</h2>
        <div className="space-y-6 text-gray-700 leading-relaxed">
          {content.bio && content.bio.split('\n').map((paragraph, index) => (
            <p key={index}>{renderInline(paragraph)}</p>
          ))}
          {hasSkills && (
            <div className="pt-4 border-t mt-6">
              <h3 className="font-serif text-2xl font-semibold text-stone-900 mb-3">Key Skills</h3>
              <ul className="list-disc list-inside space-y-2 pl-4 text-gray-600">
                {content.skills.filter(skill => skill && skill.trim() !== '').map((skill, index) => (
                  <li key={index}>{skill}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 panel">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-4xl font-semibold tracking-tight text-stone-900 border-b border-stone-200 pb-3">
          {isEditing ? 'Edit About Me' : content.title}
        </h2>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            <Edit3 className="w-4 h-4" />
            <span>Edit</span>
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              type="text"
              value={content.title}
              onChange={(e) => setContent(prev => ({ ...prev, title: e.target.value }))}
              className="field"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bio Content</label>
            <textarea
              value={content.bio}
              onChange={(e) => setContent(prev => ({ ...prev, bio: e.target.value }))}
              rows="8"
              className="field"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Skills (one per line)</label>
            <textarea
              value={content.skills.join('\n')}
              onChange={(e) => setContent(prev => ({ ...prev, skills: e.target.value.split('\n').filter(s => s.trim()) }))}
              rows="4"
              className="field"
            />
          </div>

          <div className="flex space-x-4">
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Saving...' : 'Save Changes'}</span>
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center space-x-2 px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
            >
              <X className="w-4 h-4" />
              <span>Cancel</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6 text-gray-700 leading-relaxed">
          {content.bio && content.bio.split('\n').map((paragraph, index) => (
            <p key={index}>{renderInline(paragraph)}</p>
          ))}
          {hasSkills && (
            <div className="pt-4 border-t mt-6">
              <h3 className="font-serif text-2xl font-semibold text-stone-900 mb-3">Key Skills</h3>
              <ul className="list-disc list-inside space-y-2 pl-4 text-gray-600">
                {content.skills.filter(skill => skill && skill.trim() !== '').map((skill, index) => (
                  <li key={index}>{skill}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AboutMeEditor;
